// API Configuration for AIbusinessinside
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || (
    process.env.NODE_ENV === 'production' 
      ? 'https://jxh9zuxdoe.execute-api.us-west-1.amazonaws.com/prod' 
      : 'http://localhost:3001'
  ),
  TIMEOUT: 10000,
  RETRY_ATTEMPTS: 3,
  ENDPOINTS: {
    ARTICLES: '/api/articles',
    ARCHIVES: '/api/archives',
    AI_SOLUTIONS: '/api/ai-solutions',
    USERS: '/api/users',
    SUBSCRIBERS: '/api/subscribers',
    DAILY_SUMMARIES: '/api/daily-summaries',
    HEALTH: '/health'
  }
};

// API Response Types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginationResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// AI Solutions API Response (different format)
export interface AISolutionsResponse {
  solutions: AISolution[];
  total: number;
  pagination: {
    currentPage: number;
    totalPages: number;
    count: number;
    total: number;
  };
}

// Article Types
export interface Article {
  id: number;
  title: string;
  slug: string;
  summary: string;
  source_url: string;
  source_name: string;
  author?: string;
  content?: string;
  excerpt?: string;
  keywords: string[];
  tags: string[];
  featured: boolean;
  published_at: string;
  scraped_at?: string;
  view_count: number;
  share_count: number;
  click_count: number;
  meta_title?: string;
  meta_description?: string;
  social_image_url?: string;
  brief_summary?: string;
  is_highlighted?: boolean;
  daily_summary_id?: number;
  display_order?: number;
}

// AI Solution Types
export interface AISolution {
  id: number;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  company_name: string;
  company_website?: string;
  logo_url?: string;
  category: string;
  subcategory?: string;
  industry_focus: string[];
  use_cases: string[];
  pricing_model: string;
  starting_price?: number;
  pricing_details?: string;
  free_trial: boolean;
  integration_types: string[];
  supported_platforms: string[];
  features: string[];
  benefits: string[];
  keywords: string[];
  tags: string[];
  website_url: string;
  demo_url?: string;
  documentation_url?: string;
  featured: boolean;
  verified: boolean;
  view_count: number;
  click_count: number;
  created_at: string;
}

// Archive Types
export interface WeeklyArchive {
  id: number;
  title: string;
  slug: string;
  description?: string;
  week_start_date: string;
  week_end_date: string;
  year: number;
  week_number: number;
  total_article_count: number;
  key_themes: string[];
  summary?: string;
  view_count: number;
  created_at: string;
}

// Subscriber Types
export interface EmailSubscriber {
  id: number;
  email: string;
  source: string;
  status: string;
  subscribed_at: string;
}

// Centralized API request function with error handling and retry logic
export const apiRequest = async <T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  let lastError: Error = new Error('Unknown error');

  // Retry logic
  for (let attempt = 1; attempt <= API_CONFIG.RETRY_ATTEMPTS; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

      const response = await fetch(url, {
        ...defaultOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on certain errors
      if (error instanceof Error && (
        error.name === 'AbortError' ||
        error.message.includes('404') ||
        error.message.includes('401') ||
        error.message.includes('403')
      )) {
        break;
      }

      // Wait before retry (exponential backoff)
      if (attempt < API_CONFIG.RETRY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }

  console.error('API Request failed after retries:', lastError);
  throw lastError;
};

// Helper function for query parameters
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });
  
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};
